import { detectionsApi } from '@/api/detections.api';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { JSONViewDialog } from '@/components/dialogs/JSONViewDialog';
import { useDetections } from '@/hooks/useDetections';
import type { AllOrientationsHoleSets } from '@/types/detection.types';
import { Layers, Scan } from 'lucide-react';
import { Button } from 'primereact/button';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DetectionsTable } from './DetectionsTable';

interface DetectionsPanelProps {
  projectId: string;
  modelId: string | null;
  title: string;
  modelType?: 'target' | 'blank';
  /** Called when regeneration completes successfully (so parent can e.g. refresh NC document). */
  onRegenerationComplete?: () => void;
  /** Called when regeneration starts or finishes (so parent can e.g. blur NC document panel). */
  onRegeneratingChange?: (regenerating: boolean) => void;
}

const getStorageKey = (projectId: string, modelType: string) => `regenerateTask_${projectId}_${modelType}`;

export const DetectionsPanel: FC<DetectionsPanelProps> = ({
  projectId,
  modelId,
  title,
  modelType = 'target',
  onRegenerationComplete,
  onRegeneratingChange,
}) => {
  const { t } = useTranslation();
  const { detections: allDetections, isLoading, error, refetch, deleteDetection } = useDetections(projectId);

  // Check if there's a saved task ID in localStorage (for initial state after F5)
  const storageKey = getStorageKey(projectId, modelType);
  const savedTaskId = localStorage.getItem(storageKey);

  // Filter detections by model_id
  const detections = modelId ? allDetections.filter((d) => d.model_id === modelId) : [];
  const [showAllOrientationsDialog, setShowAllOrientationsDialog] = useState(false);
  const [allOrientationsData, setAllOrientationsData] = useState<AllOrientationsHoleSets | null>(null);
  const [isLoadingAllOrientations, setIsLoadingAllOrientations] = useState(false);
  // Initialize isRegenerating based on localStorage - button is disabled immediately after F5
  const [isRegenerating, setIsRegenerating] = useState(!!savedTaskId);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Flag to prevent multiple task completions from triggering multiple refetches
  const taskCompletedRef = useRef(false);

  // Use refs to store functions to avoid dependency issues in callbacks
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    onRegeneratingChange?.(isRegenerating);
  }, [isRegenerating, onRegeneratingChange]);

  const handleDelete = async (id: string) => {
    await deleteDetection(id);
  };

  const pollTaskStatus = useCallback(
    async (taskId: string) => {
      // Prevent multiple completions
      if (taskCompletedRef.current) return;

      const key = getStorageKey(projectId, modelType);
      try {
        const status = await detectionsApi.getTaskStatus(taskId);

        if (status.status === 'completed') {
          // Mark as completed to prevent duplicate processing
          taskCompletedRef.current = true;

          // Task completed - stop polling and refetch detections
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          localStorage.removeItem(key);
          setIsRegenerating(false);
          toast.success(tRef.current('detections.regenerateCompleted'));
          refetchRef.current();
          onRegenerationComplete?.();
        } else if (status.status === 'failed') {
          // Mark as completed to prevent duplicate processing
          taskCompletedRef.current = true;

          // Task failed - stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          localStorage.removeItem(key);
          setIsRegenerating(false);
          toast.error(status.error || tRef.current('detections.regenerateFailed'));
        }
        // If pending or processing, continue polling
      } catch (_error) {
        // Error fetching status - stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        localStorage.removeItem(key);
        setIsRegenerating(false);
      }
    },
    [projectId, modelType]
  );

  // Check for ongoing regeneration task on mount (survives F5 refresh)
  // Using ref for pollTaskStatus to avoid re-running effect when callback changes
  const pollTaskStatusRef = useRef(pollTaskStatus);
  pollTaskStatusRef.current = pollTaskStatus;

  useEffect(() => {
    const key = getStorageKey(projectId, modelType);
    const taskIdFromStorage = localStorage.getItem(key);

    // Skip if already processed
    if (!taskIdFromStorage || taskCompletedRef.current) {
      return;
    }

    // Start polling immediately
    const checkTask = async () => {
      try {
        const status = await detectionsApi.getTaskStatus(taskIdFromStorage);
        if (status.status === 'completed' || status.status === 'failed') {
          // Mark as completed to prevent duplicate processing
          taskCompletedRef.current = true;

          // Task finished - clean up
          localStorage.removeItem(key);
          setIsRegenerating(false);
          if (status.status === 'completed') {
            toast.success(tRef.current('detections.regenerateCompleted'));
            refetchRef.current();
          } else {
            toast.error(status.error || tRef.current('detections.regenerateFailed'));
          }
        } else {
          // Task still running - start polling
          pollingIntervalRef.current = setInterval(() => {
            pollTaskStatusRef.current(taskIdFromStorage);
          }, 1000);
        }
      } catch (_error) {
        // Task not found or error - clean up
        localStorage.removeItem(key);
        setIsRegenerating(false);
      }
    };

    checkTask();
    // Only run on mount and when projectId/modelType changes
  }, [projectId, modelType]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleViewAllOrientations = async () => {
    if (detections.length === 0) return;

    // Use first detection to get all orientations (they all belong to the same model)
    const firstDetection = detections[0];

    setIsLoadingAllOrientations(true);
    try {
      const data = await detectionsApi.getAllHoles(firstDetection.id);
      setAllOrientationsData(data);
      setShowAllOrientationsDialog(true);
    } catch (_error) {
      toast.error(t('detections.failedToLoad'));
    } finally {
      setIsLoadingAllOrientations(false);
    }
  };

  const holesDetections = detections.filter((d) => d.extraction_type === 'holes');

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Scan size={24} className="text-primary" />
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>

      {/* Error state */}
      {error && <ErrorMessage message={error} />}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      ) : !modelId ? (
        /* No model state */
        <EmptyState title={t('detections.noModel')} message={t('detections.noModelMessage')} />
      ) : detections.length === 0 && !isRegenerating ? (
        /* Empty state */
        <EmptyState
          title={t('detections.noDetections')}
          message={modelType === 'target' ? t('detections.noDetectionsMessage') : ''}
        />
      ) : isRegenerating ? (
        /* Regenerating state */
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <LoadingSpinner size="large" />
          <p className="text-gray-500">{t('detections.regenerateStarted')}</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{t('detections.holes')}</span>
              <span className="text-xs text-gray-500">({holesDetections.length})</span>
            </div>
            <Button
              icon={<Layers size={16} />}
              onClick={handleViewAllOrientations}
              text={true}
              rounded={true}
              size="small"
              loading={isLoadingAllOrientations}
              tooltip={t('detections.allOrientations')}
              tooltipOptions={{ position: 'top' }}
            />
          </div>
          {holesDetections.length === 0 ? (
            <EmptyState title={t('detections.noDetections')} message="" />
          ) : (
            <DetectionsTable detections={holesDetections} onDelete={handleDelete} projectId={projectId} />
          )}
        </>
      )}

      {/* All orientations dialog */}
      {allOrientationsData && (
        <JSONViewDialog
          visible={showAllOrientationsDialog}
          title={t('detections.allOrientations')}
          jsonData={allOrientationsData}
          onClose={() => setShowAllOrientationsDialog(false)}
          downloadFilename="detection_all_orientations.json"
        />
      )}
    </div>
  );
};
